import { AgendaBloqueio, AgendaClassificacao, AgendaGradeLinha, AgendaResumoDia } from '../../../../interfaces/agenda';

export const LIMITE_PAGINA = 50;
export const ESPERA_ATENCAO_MIN = 30;
export const ESPERA_CRITICO_MIN = 60;
export const COR_CLASSIF_PADRAO = '#cccccc';
/** Limites >= 999 são "sem limite" no Tasy. */
const LIMITE_RETORNO_INFINITO = 999;

export type ClasseEspera = '' | 'amb' | 'verm' | 'fim';

/** Coluna ordenável: chave aceita pelo back (só campos de primeiro nível da linha). */
export interface AgendaOrdenacao {
  campo: string;
  ordem: 'asc' | 'desc';
}

export const ORDENACAO_PADRAO: AgendaOrdenacao = { campo: 'hrInicio', ordem: 'asc' };

/** 47 min / 1h20: acima de 1h, minutos puros obrigam a fazer a conta de cabeça. */
export function formatarEspera(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

/**
 * Só pinta quem ainda espera. Atendimento encerrado fica cinza: é histórico, e no fim do dia a
 * tela inteira ficaria vermelha se pintasse.
 */
export function classeEspera(l: Pick<AgendaGradeLinha, 'qtMinEspera' | 'dtFimAtendimento'>): ClasseEspera | null {
  const min = l.qtMinEspera;
  if (min === null || min === undefined) return null;
  if (l.dtFimAtendimento) return 'fim';
  if (min >= ESPERA_CRITICO_MIN) return 'verm';
  if (min >= ESPERA_ATENCAO_MIN) return 'amb';
  return '';
}

export function ehLivre(l: AgendaGradeLinha): boolean {
  return l.ieStatusAgenda === 'L' && !l.paciente?.cdPessoaFisica;
}

export function ehCancelado(l: AgendaGradeLinha): boolean {
  return l.ieStatusAgenda === 'C';
}

export function ehBloqueado(l: AgendaGradeLinha): boolean {
  return l.ieBloqueado === 'S' || l.ieStatusAgenda === 'B';
}

export function temPaciente(l: AgendaGradeLinha): boolean {
  return !!l.paciente?.cdPessoaFisica;
}

/**
 * Bloqueado com paciente continua selecionável: é justamente quem precisa ser transferido
 * depois que a agenda foi bloqueada.
 */
export function selecionavel(l: AgendaGradeLinha): boolean {
  if (ehCancelado(l)) return false;
  return !(ehBloqueado(l) && !temPaciente(l));
}

/** Agendamento vigente (alvo de alterar, transferir, cancelar). */
export function ehMarcado(l: AgendaGradeLinha): boolean {
  return temPaciente(l) && !ehCancelado(l);
}

/** Livre que aceita agendamento direto (reservado só para supervisão). */
export function aceitaAgendamento(l: AgendaGradeLinha, supervisor: boolean): boolean {
  if (ehBloqueado(l) || temPaciente(l)) return false;
  return l.ieStatusAgenda === 'L' || (supervisor && l.ieStatusAgenda === 'R');
}

/** Bloqueio total = algum bloqueio do dia sem faixa de horário. */
export function bloqueioTotal(bloqueios: AgendaBloqueio[]): boolean {
  return bloqueios.some(b => !(b.hrInicio && b.hrFinal));
}

const DIAS = ['', 'Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

export function descreverBloqueio(b: AgendaBloqueio): string {
  const partes = [`${b.dtInicial} até ${b.dtFinal}`];
  if (b.hrInicio && b.hrFinal) partes.push(`${b.hrInicio} às ${b.hrFinal}`);
  if (b.ieDiaSemana) partes.push(DIAS[b.ieDiaSemana] || String(b.ieDiaSemana));
  if (b.dsObservacao) partes.push(b.dsObservacao);
  return partes.join(' · ');
}

function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}

/**
 * A grade traz a descrição do Tasy ("Avaliação", "Exames") e a lista do back vem sem acento e
 * no singular: casa exato, depois por "contém", como o original.
 */
export function classificacaoDaLinha(ds: string | null | undefined, lista: AgendaClassificacao[]): AgendaClassificacao | null {
  if (!ds) return null;
  const alvo = normalizar(ds);
  const exata = lista.find(c => normalizar(c.valor) === alvo || normalizar(c.rotulo) === alvo);
  if (exata) return exata;
  // Mais longa primeiro: "Retorno Online" não pode cair em "Retorno".
  const porTamanho = [...lista].sort((a, b) => b.valor.length - a.valor.length);
  return porTamanho.find(c => alvo.includes(normalizar(c.valor))) ?? null;
}

export function corClassificacao(ds: string | null | undefined, lista: AgendaClassificacao[]): string {
  return classificacaoDaLinha(ds, lista)?.cor ?? COR_CLASSIF_PADRAO;
}

/**
 * O back manda consulta e exame numa lista só, marcada por tipo. Integrado (ou tipo desconhecido)
 * fica com as duas; item sem tipo vale para qualquer um. Odonto é agenda de consulta.
 */
export function classificacoesDoTipo(lista: AgendaClassificacao[], tipo: string | null | undefined): AgendaClassificacao[] {
  if (tipo === 'ODONTOLOGIA') tipo = 'CONSULTA';
  if (tipo !== 'CONSULTA' && tipo !== 'EXAME') return lista;
  return lista.filter(c => !c.tipo || c.tipo === tipo);
}

/** Para o select do filtro: no Integrado as duas listas repetem rótulos ("Consulta"). */
export function opcoesClassificacao(lista: AgendaClassificacao[], tipo: string | null | undefined): AgendaClassificacao[] {
  const vistos = new Set<string>();
  return classificacoesDoTipo(lista, tipo).filter(c => {
    const k = normalizar(c.rotulo);
    if (vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });
}

export interface LegendaClassificacao {
  rotulo: string;
  cor: string;
}

/**
 * Legenda com as classificações que aparecem na grade carregada, cada linha casada na lista do
 * seu tipo. Sem linhas, a lista do tipo do filtro.
 */
export function legendaClassificacoes(
  linhas: AgendaGradeLinha[],
  lista: AgendaClassificacao[],
  tipoFiltro: string | null | undefined,
): LegendaClassificacao[] {
  const vistos = new Map<string, LegendaClassificacao>();
  for (const l of linhas) {
    if (!l.dsClassifAgenda || ehCancelado(l)) continue;
    const c = classificacaoDaLinha(l.dsClassifAgenda, classificacoesDoTipo(lista, l.tipo));
    const rotulo = l.dsClassifAgenda.trim();
    const chave = normalizar(rotulo);
    if (!vistos.has(chave)) vistos.set(chave, { rotulo, cor: c?.cor ?? COR_CLASSIF_PADRAO });
  }
  if (vistos.size) return [...vistos.values()].sort((a, b) => a.rotulo.localeCompare(b.rotulo));
  return opcoesClassificacao(lista, tipoFiltro).map(c => ({ rotulo: c.rotulo, cor: c.cor }));
}

export interface AgendaChipResumo {
  chave: 'total' | 'ocupados' | 'livres' | 'encaixes' | 'retornos';
  valor: string;
  rotulo: string;
  tom: '' | 'ok' | 'warn' | 'roxo';
  dica?: string;
}

const RETORNOS = ['retorno', 'retorno online'];

/**
 * Tudo vem do resumo do back (agenda inteira do dia, não a grade filtrada). Retornos só para
 * consulta, com o limite da agenda quando houver.
 */
export function chipsResumo(resumo: AgendaResumoDia, linhas: AgendaGradeLinha[], tipo: string): AgendaChipResumo[] {
  const chips: AgendaChipResumo[] = [
    { chave: 'total', valor: String(resumo.horariosDia), rotulo: 'horários', tom: '' },
    { chave: 'ocupados', valor: String(resumo.horariosOcupados), rotulo: 'ocupados', tom: 'roxo' },
    { chave: 'livres', valor: String(resumo.horariosLivres), rotulo: 'livres', tom: 'ok' },
    { chave: 'encaixes', valor: String(resumo.encaixes ?? 0), rotulo: 'encaixes', tom: 'warn' },
  ];
  if (tipo === 'CONSULTA') {
    const atual = resumo.horariosRetorno ?? linhas.filter(
      l => !ehCancelado(l) && RETORNOS.includes(normalizar(l.dsClassifAgenda ?? '')),
    ).length;
    const lim = resumo.limiteRetornos;
    const valor = lim && lim < LIMITE_RETORNO_INFINITO ? `${atual}/${lim}` : String(atual);
    chips.push({ chave: 'retornos', valor, rotulo: 'retornos', tom: '' });
  }
  return chips;
}

/** Cor da pill de status: códigos sem token próprio ficam no cinza. */
const STATUS_COM_COR = new Set(['L', 'N', 'CN', 'AR', 'AC', 'A', 'O', 'AD', 'E', 'C', 'R', 'B', 'F', 'I', 'RE', 'S', 'IN', 'FI', 'M']);

export function classeStatus(st: string | null | undefined): string {
  return st && STATUS_COM_COR.has(st) ? `st-${st}` : 'st-def';
}

export function formatarCpf(cpf: string | null | undefined): string {
  const d = (cpf ?? '').replace(/\D/g, '');
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : (cpf ?? '');
}

/** Subtítulo do Paciente, como no protótipo: CPF · idade · atend. N, só com o que existir. */
export function subPaciente(l: Pick<AgendaGradeLinha, 'paciente' | 'idade'>): string {
  const partes: string[] = [];
  if (l.paciente?.nrCpf) partes.push(formatarCpf(l.paciente.nrCpf));
  if (l.idade !== null && l.idade !== undefined) partes.push(`${l.idade} anos`);
  if (l.paciente?.nrAtendimento) partes.push(`atend. ${l.paciente.nrAtendimento}`);
  return partes.join(' · ');
}

/**
 * Subtítulo do "Agendado por": só a data (dd/mm), sem o canal — a Fila ainda não grava o
 * histórico de origem (F14 do discovery, "Não existe"), então não há canal para mostrar.
 */
export function subAgendadoPor(dtAgendamento: string | null | undefined): string {
  return (dtAgendamento ?? '').slice(0, 5);
}

/** Minutos < 1h em número isolado ("min" vai no subtítulo); 1h+ usa o formato por extenso. */
export function esperaPrincipal(min: number): string {
  return min < 60 ? String(min) : formatarEspera(min);
}

export function esperaSub(min: number): string | null {
  return min < 60 ? 'min' : null;
}

/** Status da legenda: os que o operador vê no dia a dia. */
export const STATUS_LEGENDA: { valor: string; rotulo: string }[] = [
  { valor: 'L', rotulo: 'Livre' }, { valor: 'N', rotulo: 'Normal' }, { valor: 'CN', rotulo: 'Confirmada' },
  { valor: 'AR', rotulo: 'Aguardando pagamento' }, { valor: 'AC', rotulo: 'Aguardando consulta' },
  { valor: 'O', rotulo: 'Em Consulta' }, { valor: 'AD', rotulo: 'Atendido' }, { valor: 'E', rotulo: 'Executada' },
  { valor: 'F', rotulo: 'Falta justificada' }, { valor: 'I', rotulo: 'Falta não justificada' },
  { valor: 'C', rotulo: 'Cancelada' }, { valor: 'R', rotulo: 'Reservada' }, { valor: 'B', rotulo: 'Bloqueada' },
];

/** Chave do aviso de turno: uma vez por sessão por agenda. */
export function chaveTurno(cdAgenda: number, cdEstabelecimento: number): string {
  return `agenda_turno_${cdAgenda}_${cdEstabelecimento}`;
}
